/**
 * tickerInfo.js
 *
 * Catalogo de tickers (ETFs + acciones) que aparecen en los portafolios sugeridos.
 * Cada entrada tiene:
 *   name      Nombre completo del producto
 *   what      Una frase explicando QUE compras (lenguaje simple, sin jerga)
 *   kind      'etf' | 'stock'
 *
 * Usado en:
 *   - Investments.jsx (composicion de Mi Plan: muestra "VOO · 500 empresas USA  35%")
 *   - Otros lugares donde aparezcan tickers de portafolios
 *
 * Cubre los tickers usados en los 4 portafolios actuales:
 *   Guardian      BND, SCHD, VOO, GLD, VNQ
 *   Explorer      VOO, QQQ, BND, VWO, GLD, SCHD
 *   Visionary     QQQ, VOO, SOXX, PLTR, HIMS, DUOL, VWO, ARKK
 *   Income Bldr   SCHD, VYM, VOO, BND, O, KO
 */

export const TICKER_INFO = {
  // ETFs amplios (mercado total)
  VOO: { name: 'S&P 500', what: '500 empresas más grandes de USA', kind: 'etf' },
  QQQ: { name: 'Nasdaq 100', what: 'Top 100 tecnológicas (Apple, NVIDIA, Microsoft)', kind: 'etf' },
  VWO: { name: 'Mercados Emergentes', what: 'Acciones de China, India, Brasil, México', kind: 'etf' },

  // ETFs sectoriales
  SOXX: { name: 'Semiconductores', what: 'Empresas de chips (NVIDIA, AMD, TSMC)', kind: 'etf' },
  ARKK: { name: 'Innovación Disruptiva', what: 'Empresas como Tesla, Coinbase, Roku', kind: 'etf' },
  VNQ: { name: 'Bienes Raíces USA', what: 'REITs — bienes raíces que pagan rentas', kind: 'etf' },

  // ETFs defensivos
  BND: { name: 'Bonos Totales', what: 'Bonos del gobierno y empresas USA', kind: 'etf' },
  GLD: { name: 'Oro Físico', what: 'Lingotes de oro custodiados en bóvedas', kind: 'etf' },

  // ETFs de dividendos
  SCHD: { name: 'Dividend Aristocrat', what: 'Empresas USA con dividendos consistentes', kind: 'etf' },
  VYM: { name: 'Alto Dividendo', what: 'Empresas que reparten dividendos altos', kind: 'etf' },

  // Acciones individuales (apuestas concentradas)
  PLTR: { name: 'Palantir', what: 'Software de análisis de datos para gobiernos y empresas', kind: 'stock' },
  HIMS: { name: 'Hims & Hers Health', what: 'Telesalud directa al consumidor', kind: 'stock' },
  DUOL: { name: 'Duolingo', what: 'App de aprendizaje de idiomas con gamificación', kind: 'stock' },
  KO:   { name: 'Coca-Cola', what: 'Bebidas globales — el dividend aristocrat clásico', kind: 'stock' },
  O:    { name: 'Realty Income', what: 'Bienes raíces que pagan dividendo MENSUAL', kind: 'stock' },

  // Catalogo principal (los 15 stocks del Cerebro 2.0 que tambien tienen analisis individual)
  AAPL: { name: 'Apple', what: 'Ecosistema de hardware + software + servicios', kind: 'stock' },
  MSFT: { name: 'Microsoft', what: 'Software empresarial + nube Azure + IA', kind: 'stock' },
  NVDA: { name: 'NVIDIA', what: 'Chips de IA — el motor del boom tecnológico', kind: 'stock' },
  TSLA: { name: 'Tesla', what: 'Autos eléctricos + IA de manejo + energía solar', kind: 'stock' },

  // Acciones nuevas del Cerebro (27-jul-2026): las 8 más transadas de USA que faltaban
  MU:   { name: 'Micron Technology', what: 'Chips de memoria (DRAM y NAND) para IA, celulares y computadores', kind: 'stock' },
  SNDK: { name: 'SanDisk', what: 'Memoria flash y almacenamiento de datos', kind: 'stock' },
  INTC: { name: 'Intel', what: 'Procesadores y chips para computadores y servidores', kind: 'stock' },
  NOW:  { name: 'ServiceNow', what: 'Software en la nube para automatizar procesos de empresas', kind: 'stock' },
  BAC:  { name: 'Bank of America', what: 'Uno de los bancos más grandes de Estados Unidos', kind: 'stock' },
  NFLX: { name: 'Netflix', what: 'Streaming de películas y series a nivel mundial', kind: 'stock' },
  VZ:   { name: 'Verizon', what: 'Telecomunicaciones — celular e internet en Estados Unidos', kind: 'stock' },
  T:    { name: 'AT&T', what: 'Telecomunicaciones y telefonía celular en Estados Unidos', kind: 'stock' },
};

/**
 * LOS FONDOS COLOMBIANOS (Progresión) — la pata en pesos de los portafolios
 * multimoneda.
 *
 * Iván, viendo la pantalla: "explica el % pero no explica qué es cada fondo".
 * Los ETFs sí lo tenían ("VOO · S&P 500 · 500 empresas más grandes de USA") y
 * los fondos de Progresión aparecían como un nombre suelto. Pedirle a alguien
 * que apruebe plata en "Rentar II" sin decirle qué es no es informar, es
 * suponer que ya sabe.
 *
 * Se describen igual que los ETFs: qué es, en una línea, sin jerga. Las
 * rentabilidades NO van aquí — vienen vivas del cron de la Superfinanciera y
 * un número quemado en el frontend envejece sin que nadie lo note.
 */
export const FONDO_INFO = {
  'Rentar II':        { name: 'Inmobiliario', what: 'Invierte en inmuebles y construcción en Colombia; la renta viene de arriendos', kind: 'fic' },
  'Rentar 2015':      { name: 'Renta fija', what: 'Deuda colombiana con trayectoria larga — el hermano mayor de la familia Rentar', kind: 'fic' },
  'Renta Fija MP':    { name: 'Renta fija mediano plazo', what: 'Deuda del gobierno y de empresas colombianas (TES y bonos)', kind: 'fic' },
  'Renta Crédito':    { name: 'Crédito', what: 'Le presta a empresas colombianas y cobra el interés', kind: 'fic' },
  'Rentamás':         { name: 'Alto rendimiento', what: 'Cartera y obligaciones — más rendimiento y más riesgo', kind: 'fic' },
  'GS Acciones':      { name: 'Acciones Colombia', what: 'Las empresas de la bolsa colombiana', kind: 'fic' },
  'Renta Plus':       { name: 'Mixto', what: 'Combina renta fija con algo de variable', kind: 'fic' },
  'Fondo Global Vista': { name: 'Estructurado', what: 'El fondo masivo de Progresión, con exposición global', kind: 'fic' },
};

/**
 * Devuelve info enriquecida del ticker, o null si no esta en el catalogo.
 */
export function getTickerInfo(symbol) {
  if (!symbol) return null;
  return TICKER_INFO[symbol.toUpperCase()] || null;
}

/**
 * Info de una posicion venga como ETF (ticker) o como fondo colombiano
 * (nombre). Una sola puerta, para que la pantalla no tenga que saber cual es.
 */
export function getPosicionInfo(x) {
  if (!x) return null;
  if (x.fondo) return FONDO_INFO[x.fondo] || null;
  return getTickerInfo(x.ticker || x.symbol);
}

/**
 * URL del audio narrado del ticker (~20s) en S3.
 * Schema: audio/tickers/{symbol_lower}-es.mp3
 * Generado por scripts/generate_ticker_audios.js
 */
export function getTickerAudioUrl(symbol) {
  if (!symbol) return null;
  const base = import.meta.env.VITE_AUDIO_BASE_URL || 'https://beta.flowexpay.com/audio';
  return `${base}/tickers/${symbol.toLowerCase()}-es.mp3`;
}

/**
 * Tickers que tienen analisis completo en el Cerebro 2.0 (catalogo CORE_SYMBOLS).
 * Para estos, el popup muestra el boton "Ver analisis completo →".
 * Para los demas (VNQ, VYM, O, KO, PLTR, HIMS, DUOL si no estan en Cerebro), solo audio + texto.
 */
export const CEREBRO_CATALOG = new Set([
  'AAPL', 'MSFT', 'NVDA', 'TSLA', 'PLTR', 'HIMS', 'DUOL',
  'VOO', 'QQQ', 'BND', 'GLD', 'SCHD', 'SOXX', 'ARKK', 'VWO',
  // Acciones nuevas (27-jul-2026)
  'MU', 'SNDK', 'INTC', 'NOW', 'BAC', 'NFLX', 'VZ', 'T',
]);

export function hasFullCerebroAnalysis(symbol) {
  return CEREBRO_CATALOG.has(symbol?.toUpperCase());
}
